import { PostgrestClient } from "@supabase/postgrest-js";
import { Placement, PlacementWithLocation, Position } from "../types";

export class PlacementService {
  private postgrest: PostgrestClient;

  constructor(postgrest: PostgrestClient) {
    this.postgrest = postgrest;
  }

  /**
   * Get placements at a specific point in time with full aircraft data
   */
  async getPlacementsAtTime(timestamp: Date): Promise<PlacementWithLocation[]> {
    const { data, error } = await this.postgrest.rpc(
      "placements_at_time_with_aircraft",
      {
        p_timestamp: timestamp.toISOString(),
      }
    );

    if (error) {
      throw new Error(`Failed to get placements at time: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get all placements for a trip
   */
  async getPlacementsByTrip(tripId: string): Promise<Placement[]> {
    const { data, error } = await this.postgrest
      .from("trip_placement")
      .select()
      .eq("trip_id", tripId)
      .order("start_time", { ascending: true });

    if (error) {
      throw new Error(`Failed to get placements for trip: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get all placements within a time range for a specific location
   */
  async getPlacementsInTimeRange(
    startDate: Date,
    endDate: Date,
    locationId?: string,
    locationType?: "hangar" | "ramp"
  ): Promise<PlacementWithLocation[]> {
    // Use the view that includes trip and tenant data
    let query = this.postgrest
      .from("trip_placement_with_location")
      .select("*")
      .lte("start_time", endDate.toISOString())
      .gte("end_time", startDate.toISOString());

    // Filter by location if provided
    if (locationId && locationType === "hangar") {
      query = query.eq("hangar_id", locationId);
    } else if (locationId && locationType === "ramp") {
      query = query.eq("ramp_id", locationId);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(
        `Failed to get placements in time range: ${error.message}`
      );
    }

    return data || [];
  }

  /**
   * Get trip counts per date for calendar indicators
   * Efficiently aggregates trip counts by date for a specific location.
   * Multi-day trips are counted on all dates they span.
   * Returns a Map where keys are ISO date strings (YYYY-MM-DD) and values are trip counts.
   */
  async getTripCountsByDate(
    startDate: Date,
    endDate: Date,
    locationId: string,
    locationType: "hangar" | "ramp"
  ): Promise<Map<string, number>> {
    const { data, error } = await this.postgrest.rpc(
      "get_trip_counts_by_date",
      {
        p_start_date: startDate.toISOString().split("T")[0], // YYYY-MM-DD format
        p_end_date: endDate.toISOString().split("T")[0],
        p_location_id: locationId,
        p_location_type: locationType,
      }
    );

    if (error) {
      throw new Error(`Failed to get trip counts by date: ${error.message}`);
    }

    // Convert array of {date, trip_count} to Map<string, number>
    const countMap = new Map<string, number>();
    if (data) {
      data.forEach(
        (row: { date: string; trip_count: number }) => {
          countMap.set(row.date, row.trip_count);
        }
      );
    }

    return countMap;
  }

  /**
   * Get all trips with placement status, including unassigned trips
   * This uses the comprehensive view that includes trips without placements
   * Used for search functionality to ensure all scheduled aircraft are findable
   */
  async getAllTripsWithPlacementStatus(
    startDate: Date,
    endDate: Date
  ): Promise<PlacementWithLocation[]> {
    // Use the comprehensive view that includes unassigned trips
    const { data, error } = await this.postgrest
      .from("all_trips_with_placement_status")
      .select("*")
      .lte("start_time", endDate.toISOString())
      .gte("end_time", startDate.toISOString());

    if (error) {
      throw new Error(
        `Failed to get all trips with placement status: ${error.message}`
      );
    }

    return data || [];
  }

  /**
   * Find the next trip start time after the current time for a location
   */
  async getNextTripTime(
    locationId: string,
    locationType: "hangar" | "ramp",
    currentTime: Date
  ): Promise<Date | null> {
    let query = this.postgrest
      .from("trip_placement")
      .select("start_time")
      .gt("start_time", currentTime.toISOString())
      .order("start_time", { ascending: true })
      .limit(1);

    // Filter by location type
    if (locationType === "hangar") {
      query = query.eq("hangar_id", locationId);
    } else {
      query = query.eq("ramp_id", locationId);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Failed to get next trip time:", error);
      return null;
    }

    if (data && data.length > 0) {
      return new Date(data[0].start_time);
    }

    return null;
  }

  /**
   * Find the previous trip start time before the current time for a location
   * Excludes currently active trips by ensuring end_time < currentTime
   */
  async getPreviousTripTime(
    locationId: string,
    locationType: "hangar" | "ramp",
    currentTime: Date
  ): Promise<Date | null> {
    // Query for trips that have started AND ended before current time
    let query = this.postgrest
      .from("trip_placement")
      .select("start_time, end_time")
      .lt("end_time", currentTime.toISOString())
      .order("start_time", { ascending: false })
      .limit(1);

    // Filter by location type
    if (locationType === "hangar") {
      query = query.eq("hangar_id", locationId);
    } else {
      query = query.eq("ramp_id", locationId);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Failed to get previous trip time:", error);
      return null;
    }

    if (data && data.length > 0) {
      return new Date(data[0].start_time);
    }

    return null;
  }

  /**
   * Get the next trip time for a specific aircraft (by tail number)
   * Optionally filter by location and type
   */
  async getNextTripTimeForAircraft(
    tailNumber: string,
    currentTime: Date,
    locationId?: string,
    locationType?: "hangar" | "ramp"
  ): Promise<Date | null> {
    // Use RPC function or direct join query instead of nested PostgREST filtering
    let query = this.postgrest
      .from("trip_placement")
      .select(`
        start_time,
        trip!inner (
          tenant!inner (*)
        )
      `)
      .gt("start_time", currentTime.toISOString())
      .order("start_time", { ascending: true });

    // Optionally filter by location
    if (locationId && locationType) {
      if (locationType === "hangar") {
        query = query.eq("hangar_id", locationId);
      } else {
        query = query.eq("ramp_id", locationId);
      }
    }

    const { data, error } = await query;

    if (error) {
      console.error("Failed to get next trip time for aircraft:", error);
      return null;
    }

    // Filter by tail number in JavaScript since PostgREST nested filtering might not work
    const matchingTrips = data?.filter((placement: any) => {
      const placementTailNumber = placement.trip?.tenant?.tail_number;
      return placementTailNumber === tailNumber;
    });

    if (matchingTrips && matchingTrips.length > 0) {
      return new Date(matchingTrips[0].start_time);
    }

    return null;
  }

  /**
   * Get the previous trip time for a specific aircraft (by tail number)
   * Excludes currently active trips by ensuring end_time < currentTime
   * Optionally filter by location and type
   */
  async getPreviousTripTimeForAircraft(
    tailNumber: string,
    currentTime: Date,
    locationId?: string,
    locationType?: "hangar" | "ramp"
  ): Promise<Date | null> {
    // Query for trips that have ended before current time
    let query = this.postgrest
      .from("trip_placement")
      .select(`
        start_time,
        end_time,
        trip!inner (
          tenant!inner (*)
        )
      `)
      .lt("end_time", currentTime.toISOString())
      .order("start_time", { ascending: false });

    // Optionally filter by location
    if (locationId && locationType) {
      if (locationType === "hangar") {
        query = query.eq("hangar_id", locationId);
      } else {
        query = query.eq("ramp_id", locationId);
      }
    }

    const { data, error } = await query;

    if (error) {
      console.error("Failed to get previous trip time for aircraft:", error);
      return null;
    }

    // Filter by tail number in JavaScript since PostgREST nested filtering might not work
    const matchingTrips = data?.filter((placement: any) => {
      const placementTailNumber = placement.trip?.tenant?.tail_number;
      return placementTailNumber === tailNumber;
    });

    if (matchingTrips && matchingTrips.length > 0) {
      return new Date(matchingTrips[0].start_time);
    }

    return null;
  }

  /**
   * Get placements with location details for a trip
   */
  async getPlacementsWithLocation(
    tripId: string
  ): Promise<PlacementWithLocation[]> {
    const { data, error } = await this.postgrest
      .from("trip_placement_with_location")
      .select()
      .eq("trip_id", tripId)
      .order("start_time", { ascending: true });

    if (error) {
      throw new Error(
        `Failed to get placements with location: ${error.message}`
      );
    }

    return data || [];
  }

  /**
   * Get available locations for a time range
   */
  async getAvailableLocations(
    startTime: string,
    endTime: string,
    fboId: string
  ): Promise<
    Array<{
      id: string;
      name: string;
      type: "hangar" | "ramp";
    }>
  > {
    try {
      // Get all hangars and ramps for the FBO
      const [hangarsResponse, rampsResponse] = await Promise.all([
        this.postgrest.from("hangar").select("id, name").eq("fbo_id", fboId),
        this.postgrest.from("ramp").select("id, name").eq("fbo_id", fboId),
      ]);

      if (hangarsResponse.error) throw hangarsResponse.error;
      if (rampsResponse.error) throw rampsResponse.error;

      // Format response
      const locations: Array<{
        id: string;
        name: string;
        type: "hangar" | "ramp";
      }> = [];

      // Add hangars
      hangarsResponse.data?.forEach((hangar) => {
        locations.push({
          id: hangar.id,
          name: hangar.name,
          type: "hangar",
        });
      });

      // Add ramps
      rampsResponse.data?.forEach((ramp) => {
        locations.push({
          id: ramp.id,
          name: ramp.name,
          type: "ramp",
        });
      });

      // Sort by type then name
      return locations.sort((a, b) => {
        if (a.type !== b.type) {
          return a.type === "hangar" ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      });
    } catch (error: any) {
      throw new Error(`Failed to get available locations: ${error.message}`);
    }
  }

  /**
   * Assign a trip to a location with automatic gap filling for partial assignments
   * This method uses the database stored procedure for atomic, consistent operations
   */
  async assignToLocationWithGapFilling(
    tripId: string,
    locationId: string | null,
    locationType: "hangar" | "ramp" | null,
    startTime?: string,
    endTime?: string,
    excludeConflicts: boolean = true, // Default to allowing overlaps
    position?: { x: number | null; y: number | null; angle: number | null }
  ): Promise<void> {
    // Use the stored procedure for atomic assignment with gap filling
    // NOTE: This procedure only handles location assignment, not position within location
    const { data, error } = await this.postgrest.rpc(
      "assign_trip_to_location",
      {
        p_trip_id: tripId,
        p_location_id: locationId,
        p_location_type: locationType,
        p_start_time: startTime || null,
        p_end_time: endTime || null,
        p_exclude_trip_from_conflicts: excludeConflicts,
      }
    );

    if (error) {
      console.error("Failed to assign location:", error);
      throw new Error(error.message || "Failed to assign location");
    }

    // Check if the stored procedure returned an error
    if (data && !data.success) {
      console.error("Assignment failed:", data.error);
      throw new Error(data.error || "Failed to assign location");
    }

    // Log any conflicts that were detected
    if (data && data.conflicts && data.conflicts.length > 0) {
      console.warn(
        `Assignment created with ${data.conflict_count} conflict(s):`,
        data.conflicts
      );
    }
  }

  /**
   * Complete trip coverage rebuilder with auto-gap-fill
   *
   * Rebuilds complete trip coverage from desired segments with:
   * - Deterministic UUIDs (same placement = same ID always)
   * - Auto-gap-fill with unassigned placements
   * - Atomic delete/recreate operation
   * - Returns warnings for auto-filled segments
   *
   * Replaces all previous placement functions with a single atomic operation
   */
  async rebuildTripCoverage(
    tripId: string,
    segments: Array<{
      start_time: string;
      end_time: string;
      hangar_id?: string | null;
      ramp_id?: string | null;
      x?: number | null;
      y?: number | null;
      angle?: number | null;
      placement_source?: 'manual' | 'auto' | 'import';
      is_locked?: boolean;
    }>
  ): Promise<{
    success: boolean;
    message: string;
    placement_count: number;
    auto_filled_count: number;
    warnings: Array<{
      type: string;
      message: string;
      gap_start?: string;
      gap_end?: string;
    }>;
  }> {
    const { data, error } = await this.postgrest.rpc("rebuild_trip_coverage", {
      p_trip_id: tripId,
      p_segments: segments,
    });

    if (error) {
      throw new Error(`Failed to rebuild trip coverage: ${error.message}`);
    }

    if (!data || data.success !== true) {
      throw new Error(data?.message || "Failed to rebuild trip coverage");
    }

    return data;
  }

  /**
   * Helper: Move aircraft to new location (uses rebuild function)
   */
  async moveAircraftAtTime(
    tripId: string,
    newLocationId: string | null,
    locationType: "hangar" | "ramp" | null,
    changeTime: Date,
    position: Partial<Position> = {},
    isLocked: boolean = true
  ): Promise<void> {
    // Get current trip placements
    const placements = await this.getPlacementsByTrip(tripId);

    // Find placement that contains the change time
    const targetPlacement = placements.find(
      (p) =>
        new Date(p.start_time) <= changeTime &&
        new Date(p.end_time) > changeTime
    );

    if (!targetPlacement) {
      throw new Error("No placement found at the specified time");
    }

    // Create new segments: before + moved + after
    const segments = [];

    // Add any placements before the target (preserve their metadata)
    placements.forEach((p) => {
      if (new Date(p.end_time) <= changeTime) {
        segments.push({
          start_time: p.start_time,
          end_time: p.end_time,
          hangar_id: p.hangar_id,
          ramp_id: p.ramp_id,
          x: p.x,
          y: p.y,
          angle: p.angle,
          placement_source: p.placement_source || 'manual',
          is_locked: p.is_locked ?? false,
        });
      }
    });

    // Add the "before" part of split placement if needed (preserve metadata)
    if (new Date(targetPlacement.start_time) < changeTime) {
      segments.push({
        start_time: targetPlacement.start_time,
        end_time: changeTime.toISOString(),
        hangar_id: targetPlacement.hangar_id,
        ramp_id: targetPlacement.ramp_id,
        x: targetPlacement.x,
        y: targetPlacement.y,
        angle: targetPlacement.angle,
        placement_source: targetPlacement.placement_source || 'manual',
        is_locked: targetPlacement.is_locked ?? false,
      });
    }

    // Add the moved segment with manual source and locked status
    segments.push({
      start_time: changeTime.toISOString(),
      end_time: targetPlacement.end_time,
      hangar_id: locationType === "hangar" ? newLocationId : null,
      ramp_id: locationType === "ramp" ? newLocationId : null,
      x: position.x !== undefined ? position.x : null,
      y: position.y !== undefined ? position.y : null,
      angle: position.angle !== undefined ? position.angle : null,
      placement_source: 'manual',
      is_locked: isLocked,
    });

    // Add any placements after the target (preserve their metadata)
    placements.forEach((p) => {
      if (new Date(p.start_time) >= new Date(targetPlacement.end_time)) {
        segments.push({
          start_time: p.start_time,
          end_time: p.end_time,
          hangar_id: p.hangar_id,
          ramp_id: p.ramp_id,
          x: p.x,
          y: p.y,
          angle: p.angle,
          placement_source: p.placement_source || 'manual',
          is_locked: p.is_locked ?? false,
        });
      }
    });

    // Rebuild coverage with new segments
    await this.rebuildTripCoverage(tripId, segments);
  }

  /**
   * Toggle lock status on a placement
   */
  async toggleLock(placementId: string, isLocked: boolean): Promise<void> {
    // Get the placement to find its trip_id
    const { data: placement, error: fetchError } = await this.postgrest
      .from("trip_placement")
      .select("trip_id")
      .eq("id", placementId)
      .single();

    if (fetchError || !placement) {
      throw new Error(`Failed to fetch placement: ${fetchError?.message || 'Not found'}`);
    }

    // Update ALL segments for this trip (not just one segment)
    const { error } = await this.postgrest
      .from("trip_placement")
      .update({ is_locked: isLocked })
      .eq("trip_id", placement.trip_id);

    if (error) {
      throw new Error(`Failed to toggle lock: ${error.message}`);
    }
  }
}
